import { motion, useReducedMotion, useTransform } from 'motion/react';
import { ArrowRight, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { VideoModal } from './VideoModal';
import { HeroPreview } from './HeroPreview';
import { EASE_OUT } from '@/lib/motion';
import { usePageScrollY } from '@/hooks/usePageScroll';

/** Headline arrives word by word; blur bridges each word's fade so it reads as one motion */
function RevealWords({ text, delay = 0 }: { text: string; delay?: number }) {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, i) => (
        <span key={i} className="inline-block whitespace-pre">
          <motion.span
            className="inline-block"
            initial={{ opacity: 0, y: '0.35em', filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.7, delay: delay + i * 0.045, ease: EASE_OUT }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </>
  );
}

export function HeroSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const scrollY = usePageScrollY();

  // The stage starts tipped back and lies flat as you scroll into it
  const rotateX = useTransform(scrollY, [0, 420], reduce ? [0, 0] : [16, 0]);
  const scale = useTransform(scrollY, [0, 420], reduce ? [1, 1] : [0.94, 1]);

  const benefits = [t('hero.benefit1'), t('hero.benefit2'), t('hero.benefit3')];
  const fade = (delay: number) => ({
    initial: { opacity: 0, y: 12, filter: 'blur(6px)' },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    transition: { duration: 0.7, delay, ease: EASE_OUT },
  });

  return (
    <section className="relative overflow-hidden bg-background px-4 pt-32 pb-24 text-foreground md:px-8 md:pt-40 md:pb-32">
      {/* Ruled lines of a training log, fading out from the top */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[720px] opacity-40 [background-image:linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] [background-size:100%_44px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]"
      />

      <div className="relative mx-auto w-full max-w-7xl">
        <motion.p {...fade(0)} className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-brand" />
          {t('hero.eyebrow')}
        </motion.p>

        <h1 className="mt-6 max-w-[17ch] text-[44px] font-medium leading-[1.02] tracking-[-0.035em] text-balance sm:text-6xl lg:text-[80px]">
          <RevealWords text={t('hero.title')} delay={0.08} />
        </h1>

        <div className="mt-8 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <motion.p {...fade(0.35)} className="max-w-[34rem] text-lg leading-7 text-muted-foreground md:text-xl md:leading-[30px]">
            {t('hero.description')}
          </motion.p>

          <motion.div {...fade(0.45)} className="flex flex-col gap-3 lg:items-end">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/login"
                state={{ signup: true }}
                className="group press inline-flex h-12 items-center justify-center gap-2 rounded-full bg-foreground pl-6 pr-5 text-[15px] font-medium text-background hover:bg-foreground/90"
              >
                {t('hero.cta')}
                <ArrowRight size={16} strokeWidth={1.75} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
              </Link>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="press inline-flex h-12 items-center justify-center gap-2.5 rounded-full surface pl-2 pr-5 text-[15px] font-medium text-foreground hover:bg-muted"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                  <Play size={13} strokeWidth={2} className="translate-x-px fill-current" />
                </span>
                {t('hero.demo')}
              </button>
            </div>
            <p className="text-[13px] text-muted-foreground">{benefits.join(' · ')}</p>
          </motion.div>
        </div>

        {/* Stage */}
        <div className="mt-16 [perspective:1800px] md:mt-20">
          <motion.div
            initial={{ opacity: 0, y: 40, filter: 'blur(12px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 1, delay: 0.3, ease: EASE_OUT }}
            style={{ rotateX, scale, transformOrigin: '50% 0%' }}
            className="will-change-transform"
          >
            <HeroPreview />
          </motion.div>
        </div>

        <motion.div
          {...fade(1.1)}
          className="mt-16 flex flex-col gap-2 text-[13px] text-muted-foreground sm:flex-row sm:items-center sm:justify-between lg:mt-40"
        >
          <p className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-positive" />
            {t('hero.syncCaption')}
          </p>
          <p>
            {t('hero.clientPrompt')}{' '}
            <Link to="/login" className="text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground">
              {t('hero.clientLink')}
            </Link>
          </p>
        </motion.div>
      </div>

      <VideoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} videoId="4czc71B17uc" />
    </section>
  );
}
